import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useAutomatedSurveyQueries } from "@/queries/automated-surveys/queryOptions";

import { ActiveSurveyDetails, type ActiveSurveyDetailsProps } from "./ActiveSurveyDetails";

const PAGE_LIMIT = 10;

export function ActiveSurveyDetailsPage(): React.ReactNode {
  const automatedSurveyQueries = useAutomatedSurveyQueries();
  const [page, setPage] = useState<number>(0);
  const { id: surveyId } = useParams<{ id: string }>();

  const activeSurveyQueryParams = {
    Offset: page * PAGE_LIMIT,
    Limit: PAGE_LIMIT,
  };

  const {
    data: automatedSurveyDetails,
    isPending: isPendingAutomatedSurveyDetails,
    error: automatedSurveyError,
  } = useQuery(automatedSurveyQueries.details(surveyId!));

  const {
    data: activeSurveys,
    isFetching: isFetchingActiveSurveys,
    error: activeSurveysError,
  } = useQuery(automatedSurveyQueries.activeList(surveyId!, activeSurveyQueryParams));

  const error = automatedSurveyError || activeSurveysError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isPendingAutomatedSurveyDetails) {
    return <FullSizeLoader withPadding />;
  }

  const totalPages = activeSurveys ? Math.ceil(activeSurveys.total / PAGE_LIMIT) : 0;

  const contentProps: ActiveSurveyDetailsProps = {
    surveyTitle: automatedSurveyDetails.title,
    surveyType: automatedSurveyDetails.category,
    activeSurveys: activeSurveys?.items ?? [],
    isLoadingActiveSurveys: isFetchingActiveSurveys,
    page,
    onPageChange: setPage,
    totalPages,
  };

  return <ActiveSurveyDetails {...contentProps} />;
}
